import { z } from 'zod';
import { MeterRoleSchema } from './assets.js';

/**
 * Manual / field-capture contract (CLAUDE.md §8.2 file/manual sources, §11 PWA).
 *
 * Single source of truth for the operator field-capture payloads — imported by the Worker
 * `POST /api/readings/manual` route, its tests, and the React `/capture` PWA screen. This is a
 * **cloud-inbound** path (an authenticated operator writing a value), entirely separate from the
 * edge-agent `/ingest` pipeline and the one-way OT boundary (§12.3): a human reads an acetylene
 * cylinder gauge, a chemical drum level, or a tank dip and records it here. The value lands in
 * `readings_1m` with `quality = 'substituted'` (§8.3) so it is never confused with a metered OT
 * sample, and every write is audited (§12.3).
 *
 * Offline-first: the PWA queues captures in IndexedDB while disconnected and flushes them as a
 * **batch** on reconnect. Writes are idempotent on `(tagId, capturedAt, actor)` so a retried flush
 * never double-counts — the route reports each row as written or deduped. Optional `clientId` is
 * echoed back so the client can reconcile its queue against the server result.
 */

/** Max rows in one offline-flush batch (keeps the request and the D1 write bounded). */
export const MANUAL_BATCH_MAX = 200;
/** Max bytes for an attached capture photo (cylinder/tank gauge snapshot → R2 evidence). */
export const MANUAL_PHOTO_MAX_BYTES = 5 * 1024 * 1024;

/** Content types accepted for a capture photo. */
export const ManualPhotoContentTypeSchema = z.enum(['image/jpeg', 'image/png', 'image/webp']);
export type ManualPhotoContentType = z.infer<typeof ManualPhotoContentTypeSchema>;

// ── Capture input (one human-recorded value) ─────────────────────────────────
export const ManualReadingInputSchema = z.object({
  /** Manual tag being captured (must be a tag with `manual = true`; route rejects otherwise). */
  tagId: z.string().min(1),
  /** The recorded value in the tag's engineering unit. */
  value: z.number().finite(),
  /**
   * When the human took the reading (ISO-8601). For an offline capture this is the original
   * capture time, not the flush time — it becomes the row's `source_ts`. The route clamps it to a
   * sane window (not in the future, not absurdly old) and stamps its own `ingest_ts` (§9).
   */
  capturedAt: z.string().datetime(),
  /** Optional free-text note (e.g. "cylinder #3, regulator replaced"). */
  note: z.string().max(500).optional(),
  /** R2 evidence key returned by a prior photo upload, linking a gauge snapshot to this reading. */
  photoKey: z.string().max(512).optional(),
  /** Client-generated queue id (offline dedupe); opaque to the server, echoed back in the result. */
  clientId: z.string().max(64).optional(),
});
export type ManualReadingInput = z.infer<typeof ManualReadingInputSchema>;

/** Offline-flush batch: one or more captures posted together. */
export const ManualReadingBatchSchema = z.object({
  readings: z.array(ManualReadingInputSchema).min(1).max(MANUAL_BATCH_MAX),
});
export type ManualReadingBatch = z.infer<typeof ManualReadingBatchSchema>;

/** Outcome for one captured row. `deduped` = an identical `(tagId, capturedAt, actor)` already existed. */
export const ManualReadingStatusSchema = z.enum(['written', 'deduped', 'rejected']);
export type ManualReadingStatus = z.infer<typeof ManualReadingStatusSchema>;

export const ManualReadingResultSchema = z.object({
  tagId: z.string(),
  /** Server-resolved capture timestamp as epoch ms (post-clamp), matching the stored `readings_1m.ts`. */
  ts: z.number(),
  /** Echoed client queue id when supplied, else null. */
  clientId: z.string().nullable(),
  status: ManualReadingStatusSchema,
  /** Human-readable reason when `status = 'rejected'` (unknown tag, not a manual tag, bad window). */
  reason: z.string().nullable(),
});
export type ManualReadingResult = z.infer<typeof ManualReadingResultSchema>;

/** Batch response: per-row results plus rollup counts. */
export const ManualReadingResponseSchema = z.object({
  written: z.number().int().nonnegative(),
  deduped: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  results: z.array(ManualReadingResultSchema),
});
export type ManualReadingResponse = z.infer<typeof ManualReadingResponseSchema>;

/** Response from the multipart photo upload (`POST /api/readings/manual/photo`) → R2 key. */
export const ManualPhotoResponseSchema = z.object({
  photoKey: z.string(),
});
export type ManualPhotoResponse = z.infer<typeof ManualPhotoResponseSchema>;

// ── Manual-tag listing (drives the capture screen's tag picker) ──────────────
/** One capturable tag with its location + last known value, for the PWA picker. */
export const ManualTagSchema = z.object({
  id: z.string(),
  name: z.string(),
  engUnit: z.string(),
  /** Owning meter role when the tag's asset has a meter (acetylene/chemical/tank_level), else null. */
  role: MeterRoleSchema.nullable(),
  /** Human-readable location ("South Deep · Surface · Acetylene store"). */
  location: z.string(),
  /** Last recorded value (any source), or null if never captured. */
  lastValue: z.number().nullable(),
  /** Epoch ms of the last recorded value, or null. */
  lastCapturedAt: z.number().nullable(),
});
export type ManualTag = z.infer<typeof ManualTagSchema>;

export const ManualTagListResponseSchema = z.object({
  siteId: z.string(),
  tags: z.array(ManualTagSchema),
});
export type ManualTagListResponse = z.infer<typeof ManualTagListResponseSchema>;
